import { isEmpty } from 'radash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import type { OpenItemEditorParams } from '@/hooks/useDialogHost';
import { useDialogHost } from '@/hooks/useDialogHost';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';

const CUSTOM_ITEM_EDITOR_SETTINGS = 'CustomItemEditorSettings';
const NO_CATEGORY = 'NO_CATEGORY';

type TemplateAttributes = Record<string, unknown>;

function angularRoot(): {
    callService?: (...args: unknown[]) => unknown;
    useNextEditor?: () => boolean;
    user?: unknown;
    organization?: unknown;
    $on?: (event: string, handler: () => void) => () => void;
} | undefined {
    try {
        return getRootScope(getInjector());
    } catch {
        return undefined;
    }
}

/** `$routeParams.category`, with `NO_CATEGORY` and a missing value as `null`. */
export function readRouteCategory(): string | null {
    try {
        const category = getInjector().get('$routeParams')?.category;
        if (category == null || category === '' || category === NO_CATEGORY) {
            return null;
        }
        return String(category);
    } catch {
        return null;
    }
}

function templateAttributesFor(category: string): TemplateAttributes {
    const root = angularRoot();
    if (!root || typeof root.callService !== 'function') {
        return {};
    }
    const map = root.callService(
        CUSTOM_ITEM_EDITOR_SETTINGS,
        'preparedDefaultItemTemplateAttributes',
        category,
        root.user,
        root.organization,
    );
    return (map as TemplateAttributes) || {};
}

/** A missing hook is null from `callService` and stays off. */
function editorSettingEnabled(method: string): boolean {
    const root = angularRoot();
    if (!root || typeof root.callService !== 'function') {
        return false;
    }
    return !!root.callService(
        CUSTOM_ITEM_EDITOR_SETTINGS,
        method,
        root.user,
        root.organization,
    );
}

/** Absent Angular shell (tests, Storybook) uses the React editor. */
function shouldUseNextEditor(): boolean {
    const root = angularRoot();
    if (!root || typeof root.useNextEditor !== 'function') {
        return true;
    }
    return root.useNextEditor() !== false;
}

function openLegacyEditor(params: Record<string, unknown>) {
    const injector = getInjector();
    const $modal = injector.get('$modal');
    const $location = injector.get('$location');
    const $timeout = injector.get('$timeout');
    const run = () => {
        // The chosen category belongs to the editor params. Writing it onto
        // the browse URL switches All / Uncategorized to that category.
        if (typeof $location.displayUrl === 'function') {
            $location.displayUrl('/edit');
        } else {
            $location.path('/edit');
        }
        $modal.open({
            templateUrl: 'tpl/editor.tpl.html',
            controller: 'EditorController',
            backdrop: true,
            keyboard: false,
            windowClass: 'item-editor',
            resolve: {
                params: () => params,
            },
        });
    };
    if (typeof $timeout === 'function') {
        $timeout(run);
    } else {
        run();
    }
}

const useCreateNewItem = (openNewItem: (params: Partial<OpenItemEditorParams>) => void) => {
    const { auth } = useAuth();
    const dialogHost = useDialogHost();
    const [routeVersion, setRouteVersion] = useState(0);

    useEffect(() => {
        const root = angularRoot();
        if (!root || typeof root.$on !== 'function') {
            return undefined;
        }
        const bump = () => setRouteVersion((version) => version + 1);
        const offs = ['$routeUpdate', '$routeChangeSuccess', 'userRolesLoaded'].map((event) =>
            root.$on?.(event, bump),
        );
        return () => offs.forEach((off) => off?.());
    }, []);

    const category = useMemo(() => {
        // `$routeParams` is mutated in place; `routeVersion` is the signal to re-read it.
        void routeVersion;
        return readRouteCategory();
    }, [routeVersion]);

    const visible = !!auth && auth.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_UI, 'create.items');
    const disabled = !auth || !auth.hasAnyPermission(
        AUTH_OBJECT_TYPES.OBJECT_TYPE_ITEMS,
        'create',
        { category__: category } as unknown as Record<string, string>,
    );

    const openPrepared = useCallback((selectedCategory: string, template: TemplateAttributes) => {
        if (!shouldUseNextEditor()) {
            openLegacyEditor({
                category: selectedCategory,
                templateForPreparedDefaultItem: template,
                fromPreparedDefaultItem: true,
            });
            return;
        }
        openNewItem({
            category: selectedCategory,
            templateForPreparedDefaultItem: template,
            fromPreparedDefaultItem: true,
            primaryKey: '',
            originalPrimaryKey: '',
        });
    }, [openNewItem]);

    const openPlain = useCallback(() => {
        if (!shouldUseNextEditor()) {
            openLegacyEditor({ category: category ?? undefined });
            return;
        }
        openNewItem({
            category: category ?? undefined,
            primaryKey: '',
            originalPrimaryKey: '',
        });
    }, [category, openNewItem]);

    const submitPrepared = useCallback((fallbackCategory: string | undefined, values?: object) => {
        const templateValues = { ...(values as TemplateAttributes) };
        const selectedCategory = String(templateValues.category__ ?? fallbackCategory ?? '');
        delete templateValues.category__;
        if (!selectedCategory) {
            return;
        }
        // DialogHost closes the prior-item dialog in the same turn as onSubmit.
        setTimeout(() => openPrepared(selectedCategory, templateValues), 0);
    }, [openPrepared]);

    const createNewItem = useCallback(() => {
        if (disabled) {
            return;
        }
        if (!editorSettingEnabled('enablePreparedDefaultItemTemplate')) {
            openPlain();
            return;
        }
        if (category) {
            const template = templateAttributesFor(category);
            if (isEmpty(template)) {
                openPrepared(category, {});
                return;
            }
            dialogHost.openPriorItem({
                dialogTitle: 'NEW_ITEM',
                isCategorySpecific: true,
                preselectedCategory: category,
                getTemplateAttributesMap: (selectedCategory?: string) =>
                    templateAttributesFor(selectedCategory || category),
                onSubmit: (values?: object) => submitPrepared(category, values),
            });
            return;
        }
        // All and Uncategorized. The category is not preselected, and it is
        // not written into the browse URL or the React browse query.
        if (!editorSettingEnabled('enablePreparedDefaultItemWithoutCategory')) {
            openPlain();
            return;
        }
        dialogHost.openPriorItem({
            dialogTitle: 'NEW_ITEM',
            isCategorySpecific: true,
            getTemplateAttributesMap: (selectedCategory?: string) =>
                (selectedCategory ? templateAttributesFor(selectedCategory) : {}),
            onSubmit: (values?: object) => submitPrepared(undefined, values),
        });
    }, [category, dialogHost, disabled, openPlain, openPrepared, submitPrepared]);

    return { visible, disabled, createNewItem };
};

export { useCreateNewItem };
